// The reminders screen: every one-off thing, to do and finished for good.
//
// A PUSHED SCREEN, NOT A TAB (prototype-v2.md section 1.2). The navigation is
// Today, Notes, Progress and Board, with Settings beside them, and Reminders is
// reached from the block on the day - exactly as Settings is reached from the
// account button, which is why `screens.ts` already had to hold the distinction
// between a screen that is a tab and one that is not. The captain settled the
// arrangement whole on 2026-08-18; `screens.ts` carries it.
//
// It is also the only place the whole list exists. The block on the day shows
// two rows on purpose, so this screen is what stops that cap being a place
// where things quietly disappear - and its Back control is the way out of a
// screen the tab bar cannot answer for.

import { Icon } from '../day/Icon.tsx';
import type { IsoDay } from '../api/client.ts';
import { ReminderRow } from './ReminderRow.tsx';
import { splitReminders } from './reminders-model.ts';
import { useVoice, wording } from '../shell/voice.ts';
import { useReminders } from './useReminders.ts';

interface Props {
  /**
   * Bumped by every write. This screen holds a read of its own - every
   * reminder there is, which no day payload carries - so a create or a remove
   * from elsewhere has to reach it, and this is how.
   */
  revision: number;
  /** The account's today: every date on a row is relative to it. */
  today: IsoDay | null;
  onBack: () => void;
  /** Opens the new-reminder sheet. */
  onNew: () => void;
  /** Opens one reminder's edit sheet. */
  onEdit: (id: string) => void;
  /** Takes one off the list, with an Undo. */
  onRemove: (id: string, title: string) => void;
  /** Any 401, from this screen's own read or from a tick made on it. */
  onSignedOut: () => void;
  /** The transient line at the foot of the screen. */
  say: (message: string) => void;
  /** Said after a tick here, so the day screen's block catches up. */
  refresh: () => void;
}

export function RemindersScreen({
  revision,
  today,
  onBack,
  onNew,
  onEdit,
  onRemove,
  onSignedOut,
  say,
  refresh,
}: Props) {
  // The read is the screen's, not the shell's: it is one GET, and it is only
  // made by opening the screen. Holding it above would put a request most
  // people never need on every load of the day.
  const { rows, problem, canToggle, toggle } = useReminders({
    revision,
    today,
    onSignedOut,
    say,
    refresh,
  });
  const groups = rows === null ? null : splitReminders(rows);
  // The two empty-state sentences and the all-clear line. Everything else on
  // this screen - the group headings, the dates on each row, the two lines that
  // report a failure - is operational or a fact and is plain in both voices.
  const voice = useVoice();

  return (
    <>
      <div className="topbar">
        <button className="iconbtn" aria-label="Back" onClick={onBack}>
          <Icon name="chevL" />
        </button>
        {/* The screen's own heading, at the top of its heading order, the way
            every other screen in this client starts with one. */}
        <h1>Reminders</h1>
        {/* THE WAY TO MAKE ONE, at every width. The floating + belongs to the
            day and is not drawn here, and the sidebar's add action goes to the
            day before it opens - so without this button the screen that IS the
            reminders list would be the one place a reminder could not be
            added. */}
        <button className="iconbtn" aria-label="New reminder" onClick={onNew}>
          <Icon name="plus" />
        </button>
      </div>

      <div className="scroll">
        <div className="wrap">
          {rows === null && problem === null && (
            <p className="sheetwait" role="status">
              Loading…
            </p>
          )}

          {groups !== null && rows !== null && rows.length === 0 && (
            <div className="empty">
              <h3>{wording('rem.empty', voice)}</h3>
              <p>{wording('rem.emptyBody', voice)}</p>
              <button className="btn primary" onClick={onNew}>
                <Icon name="plus" />
                Add a reminder
              </button>
            </div>
          )}

          {groups !== null && rows !== null && rows.length > 0 && (
            <>
              {/* Said once, where a tick is about to be pressed and cannot be
                  written. The list is still readable, which is why this is a
                  line rather than a screen: a reminder you can read and not
                  tick is more use than "Loading…" over all of them. */}
              {!canToggle && (
                <p className="sheetproblem" role="status">
                  Your day has not arrived yet, so nothing here can be ticked off. A reminder is
                  recorded against the day you did it.
                </p>
              )}

              <div className="groupline">To do</div>
              {groups.open.length > 0 ? (
                <section className="card rem">
                  <div className="card-body">
                    <div>
                      <ul>
                        {groups.open.map((reminder) => (
                          <ReminderRow
                            key={reminder.id}
                            reminder={reminder}
                            today={today}
                            onToggle={canToggle ? toggle : null}
                            onEdit={onEdit}
                            onRemove={onRemove}
                          />
                        ))}
                      </ul>
                    </div>
                  </div>
                </section>
              ) : (
                <section className="card rem">
                  <div className="remnote">{wording('rem.allDone', voice)}</div>
                </section>
              )}

              {groups.finished.length > 0 && (
                <>
                  <div className="groupline">Finished</div>
                  <section className="card rem">
                    <div className="card-body">
                      <div>
                        <ul>
                          {groups.finished.map((reminder) => (
                            <ReminderRow
                              key={reminder.id}
                              reminder={reminder}
                              today={today}
                              onToggle={canToggle ? toggle : null}
                              onEdit={onEdit}
                              onRemove={onRemove}
                            />
                          ))}
                        </ul>
                      </div>
                    </div>
                  </section>
                  {/* The screen says its own rule, once, where the finished
                      ones are - and says the thing this product is careful
                      about out loud: nothing here is a streak (AGENTS.md,
                      "Counts, never streaks"). */}
                  <p className="finetext">
                    A finished reminder is finished for good. It does not come back tomorrow, and
                    nothing here is a streak you can break.
                  </p>
                </>
              )}
            </>
          )}

          {/* The read failed and whatever was last on screen is still there, so
              this says which of the two you are looking at rather than
              replacing a list that may still be right. */}
          {problem !== null && (
            <p className="sheetproblem" role="alert">
              {rows === null ? problem : `This list may be out of date. ${problem}`}
            </p>
          )}
        </div>
      </div>
    </>
  );
}
